import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';

import { useAdaptiveSmartCardVariant } from '../../../hooks/use-adaptive-smart-card-variant';
import type { LayoutRendererProps } from '../types';
import { getElementKey } from '../utils';

const columnStyles = cssMap({
	leftColumnStyles: {
		flexGrow: 1,
		flexShrink: 1,
		minWidth: '0px',
	},
	rightColumnStyles: {
		flexShrink: 0,
		minWidth: '0px',
	},
});

/**
 * Two-columns layout component
 * Renders elements in a left and right column with adaptive smart card sizing
 */
export const TwoColumnsLayout = React.memo(
	(props: LayoutRendererProps): React.JSX.Element | null => {
		const {
			textContentRef,
			leftNeedsMeasurement,
			rightNeedsMeasurement,
			leftElements,
			rightElements,
			cardVariant,
		} = useAdaptiveSmartCardVariant(props.content);

		if (props.content.length < 2) {
			return null;
		}

		return (
			<Inline space="space.200" grow="fill">
				<Box
					xcss={columnStyles.leftColumnStyles}
					ref={leftNeedsMeasurement ? textContentRef : undefined}
					testId="two-column-layout-left"
				>
					{leftElements.map((element, index) => (
						<React.Fragment
							key={getElementKey({ key: props.sectionKey, pos: element.attributes?.pos, index })}
						>
							{props.renderElement({
								component: element,
								position: 'two-columns-left',
								variant: cardVariant,
								isFoldable: props.isFoldable,
							})}
						</React.Fragment>
					))}
				</Box>
				<Box
					xcss={columnStyles.rightColumnStyles}
					ref={rightNeedsMeasurement ? textContentRef : undefined}
					testId="two-column-layout-right"
				>
					{rightElements.map((element, index) => (
						<React.Fragment
							key={getElementKey({ key: props.sectionKey, pos: element.attributes?.pos, index })}
						>
							{props.renderElement({
								component: element,
								position: 'two-columns-right',
								variant: cardVariant,
								isFoldable: props.isFoldable,
							})}
						</React.Fragment>
					))}
				</Box>
			</Inline>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.content === nextProps.content &&
			prevProps.isFoldable === nextProps.isFoldable &&
			prevProps.isStreaming === nextProps.isStreaming &&
			prevProps.subAgentDataMap === nextProps.subAgentDataMap
		);
	},
);
